import { useEffect } from "react";
import { Link } from "@tanstack/react-router";

import { Button } from "#/components/ui/button";

type ErrorPageProps = {
  code: "404" | "500";
  title: string;
  description: string;
  showReload?: boolean;
};

function ErrorPage({
  code,
  title,
  description,
  showReload = false,
}: ErrorPageProps) {
  return (
    <Dialog title={title}>
      <Line
        prefix="!"
        className={code === "404" ? "text-amber-300" : "text-red-400"}
      >
        ERR {code}
      </Line>
      <Line prefix="$" className="text-foreground">
        {title}
      </Line>
      <Blank />
      <Line prefix="#" className="text-muted-foreground">
        {description}
      </Line>
      <Blank />
      <Line prefix=">" className="text-muted-foreground/60 text-xs">
        Use the actions below to recover.
      </Line>
      <Blank />
      <div className="flex flex-wrap justify-end gap-3 pl-6">
        <Link to="/" preload={false}>
          <Button>[back home]</Button>
        </Link>
        {showReload ? (
          <Button variant="outline" onClick={() => window.location.reload()}>
            [try again]
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
}

export function NotFoundPage() {
  return (
    <ErrorPage
      code="404"
      title="This page slipped out of route."
      description="The address does not match anything in GoodSender right now. Head back home and we can get you moving again."
    />
  );
}

/**
 * Rendered both by the `/500` route (no error) and as the root route's
 * `errorComponent` (TanStack passes `{ error }`). TanStack catches route render
 * errors before they reach Sentry's global handler, so we report them here when
 * an error is present.
 */
export function ServerErrorPage({ error }: { error?: unknown }) {
  useEffect(() => {
    if (!error) return;
    // Lazy-load so the Sentry SDK stays out of the Node prerender bundle; this
    // effect is client-only and resolves to the instance already initialized in
    // main.tsx.
    void import("@sentry/react").then((Sentry) => {
      Sentry.captureException(error);
    });
  }, [error]);

  return (
    <ErrorPage
      code="500"
      title="Something went wrong on our side."
      description="An unexpected error interrupted this page. Try reloading, and if it keeps happening we should inspect the failing route or request next."
      showReload
    />
  );
}

function Dialog({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex min-h-screen flex-1 flex-col items-center justify-start overflow-hidden px-4 pt-[25vh] sm:px-6">
      <div className="w-full max-w-lg overflow-hidden rounded border border-border/80 shadow-[0_24px_80px_rgba(2,6,23,0.5)]">
        <div className="flex items-center gap-2 border-b border-border/60 bg-card/90 px-4 py-2.5 backdrop-blur">
          <span className="flex-1 text-center font-mono text-xs tracking-wide text-muted-foreground">
            {title}
          </span>
        </div>
        <div className="space-y-1.5 bg-[rgba(2,6,23,0.85)] px-5 py-6 font-mono text-sm leading-relaxed backdrop-blur sm:px-6">
          {children}
        </div>
      </div>
    </div>
  );
}

function Line({
  prefix,
  className,
  children,
}: {
  prefix: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex gap-2 ${className ?? ""}`}>
      <span className="w-4 shrink-0 text-right text-muted-foreground/60">
        {prefix}
      </span>
      <span>{children}</span>
    </div>
  );
}

function Blank() {
  return <div className="h-1.5" />;
}
